import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    skip,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';

import { CoachingType } from '../../coaching-base/general';
import {
    OpportunityType,
    OpportunityView,
} from '../../coaching-base/opportunity';
import { CoachingSwitchboardService } from '../../coaching-base/switchboard';
import { GraphDefinition } from '../../coaching-insight/line-area.component';
import { actions, addPageAction } from '../../coaching/actions';
import { CmpsDecimalPipe } from '../../common/number.pipe';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { PeriodType } from '../../widget/date-range.widget.model';
import {
    matchUserId,
    navigateToNewDraftCommitment,
    navigateToNewObservation,
} from '../navigate';
import { CommitmentSummaryView } from '../pillar-two';
import { watchForNumberParamChange, watchForUserChange } from '../watch-route';

import { defaultGraphSet, setupChart } from './chart';
import { ChartType } from './chart-type.model';
import {
    reloadChart,
    reloadChartCancel,
    reloadDetail,
    reloadDetailCancel,
    selectChart,
} from './pillar-three.action';
import { TranslationKeyMap } from './pillar-three.i18n';
import { coachingDetailPillarThreeReducer } from './pillar-three.reducer';
import { PillarThreeState } from './pillar-three.state';

@Component({
    selector: 'cmps-coaching-detail-pillar-three',
    templateUrl: './pillar-three.component.html',
    host: {
        class: 'cmps-coaching-detail-pillar-three',
    },
})
@TakeUntilDestroy()
@UseStore<PillarThreeState>(
    'coachingDetail.pillarThree',
    coachingDetailPillarThreeReducer,
)
export class CoachingDetailPillarThreeComponent
    implements OnInit, OnDestroy, StateToProps<PillarThreeState>
{
    state: PillarThreeState;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _ChartType = ChartType;
    /** @private */ _PeriodType = PeriodType;
    /** @private */ _opportunity: OpportunityView;

    // TODO move into reducer
    /**
     * @private
     * temporary
     */
    _graphSetSGToggles: boolean[];

    /**
     * @private
     * temporary
     */
    _graphSet: GraphDefinition[];

    /**
     * @private
     * when 'ready' is emitted from both pillars 1 and 2
     */
    _siblingPillarsReady: boolean;

    /** @private */
    get _coached(): boolean {
        return this._switchboardService.pillarTwo.sbSelectedOpportunityHasActiveCommitment();
    }

    /** @private */
    get isPending(): boolean {
        return (
            this._opportunity &&
            this._opportunity.oppType === OpportunityType.PENDING
        );
    }

    constructor(
        private readonly _router: Router,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _translateService: TranslateService,
        private readonly _decimalPipe: CmpsDecimalPipe,
        private readonly _dialog: CmpsDialog,
        private readonly _switchboardService: CoachingSwitchboardService,
    ) {
        this._graphSetSGToggles = [true];
    }

    ngOnInit(): void {
        watchForUserChange(this._activatedRoute)
            .pipe(
                tap(() => {
                    this._checkData(null);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._watchForSiblingPillars();
    }

    ngOnDestroy(): void {
        useDispatcher(this, reloadChartCancel({}));
        useDispatcher(this, reloadDetailCancel({}));
    }

    stateToProps(prevState?: PillarThreeState): void {
        if (prevState.graphSet !== this.state.graphSet) {
            this._checkData(null);
        }
    }

    propsToDispatch(): void {
        this._checkData(null);
    }

    /** @private */
    _onStartObservationClick(generic: boolean): void {
        const pageActionAttrs = {
            generic,
            metricId: this._opportunity?.metricId,
            coachingTypeId: this._opportunity?.coachingType,
        };
        addPageAction(actions.PillarThree.START_OBS_CLICK, pageActionAttrs);

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9505_DO_YOU_WANT_TO_CREATE_A_NEW_OBSERVATION_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 0) {
                        addPageAction(
                            actions.PillarThree.START_OBS_YES_CLICK,
                            pageActionAttrs,
                        );
                    }
                    if (ret === 1) {
                        addPageAction(
                            actions.PillarThree.START_OBS_NO_CLICK,
                            pageActionAttrs,
                        );
                    }
                    if (ret == null) {
                        addPageAction(
                            actions.PillarThree.START_OBS_DISMISS,
                            pageActionAttrs,
                        );
                    }

                    if (ret === 0) {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        navigateToNewObservation(
                            this._router,
                            this._activatedRoute,
                            generic,
                            undefined,
                            undefined,
                            undefined,
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onStartCommitmentClick(generic: boolean): void {
        const pageActionAttrs = {
            generic,
            metricId: this._opportunity?.metricId,
            coachingTypeId: this._opportunity?.coachingType,
        };
        addPageAction(actions.PillarThree.START_CMT_CLICK, pageActionAttrs);

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9556_DO_YOU_WANT_TO_CREATE_A_NEW_COMMITMENT_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        const oppIsAIsUncoachedAndIsGeneric =
            !generic &&
            this._opportunity &&
            this._opportunity.coachingType === CoachingType.UNCOACHED;
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 0) {
                        addPageAction(
                            actions.PillarThree.START_CMT_YES_CLICK,
                            pageActionAttrs,
                        );
                    }
                    if (ret === 1) {
                        addPageAction(
                            actions.PillarThree.START_CMT_NO_CLICK,
                            pageActionAttrs,
                        );
                    }
                    if (ret == null) {
                        addPageAction(
                            actions.PillarThree.START_CMT_DISMISS,
                            pageActionAttrs,
                        );
                    }

                    if (ret === 0) {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        navigateToNewDraftCommitment(
                            this._router,
                            this._activatedRoute,
                            !oppIsAIsUncoachedAndIsGeneric,
                            undefined,
                            undefined,
                            undefined,
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onCloseToPulseClick(): void {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._router.navigate(['pulse']);
    }

    /** @private */
    _onChartTypeChange(tabRefId: string): void {
        this._dispatch(tabRefId as ChartType, null, null);
    }

    /** @private */
    _onPeriodTypeClick(periodTypeId: PeriodType): void {
        this._dispatch(null, periodTypeId, null);
    }

    /**
     * temporary. this will change as line-area-chart is refactored
     * @param chartType ChartType to assume if no real data is present. defaults to state's chartType
     */
    private _checkData(chartType: ChartType): void {
        if (!this._opportunity || !this.state.graphSet) {
            chartType = chartType == null ? this.state.chartType : chartType;
            this._graphSet = defaultGraphSet(chartType);
            this._graphSet[0].options = setupChart(
                null,
                null,
                chartType,
                null,
                true, // we set this to true even though defaultGraphSet has showEmptyGraph as false
                this._translateService,
                this._decimalPipe,
            );
            return;
        }
        this._graphSet = [
            {
                ...this.state.graphSet[0],
                options: setupChart(
                    this._opportunity.label,
                    this._opportunity.metricId,
                    this.state.chartType,
                    this.state.periodTypeId,
                    this.state.graphSet[0].showEmptyGraph,
                    this._translateService,
                    this._decimalPipe,
                ),
            },
        ];
    }

    private _dispatch(
        chartType: ChartType,
        periodTypeId: PeriodType,
        metricId: number,
    ): void {
        useDispatcher(this, reloadChartCancel({}));
        useDispatcher(this, reloadDetailCancel({}));

        const userId = parseInt(matchUserId(this._router.url)[1]);
        let nextPeriodTypeId =
            periodTypeId != null ? periodTypeId : this.state.periodTypeId;

        // specific check for undefined
        const nextMetricId =
            metricId === undefined || metricId != null
                ? metricId
                : this.state.metricId;

        // specific check for undefined
        let nextChartType =
            chartType === undefined || chartType != null
                ? chartType
                : this.state.chartType;
        const isNonKpi =
            this._activatedRoute.snapshot.queryParams.isNonKpi === 'true' ||
            this._activatedRoute.snapshot.queryParams.isNonKpi === '1';
        if (nextChartType == null) {
            nextChartType =
                nextMetricId != null && this._coached
                    ? ChartType.TREND
                    : ChartType.VARIANCE;
        }
        nextChartType = isNonKpi ? ChartType.TREND : nextChartType;

        const cmt =
            this._switchboardService.pillarTwo.sbSelectedOpportunityActiveCommitmentSnapshot();
        const nextCommitmentId = cmt
            ? cmt.commitmentId
            : this.state.commitmentId;
        const nextGoalIndex = cmt
            ? cmt.goals.findIndex((e) => e.metricId === nextMetricId)
            : -1;
        const nextGoalId =
            nextGoalIndex !== -1 ? cmt.goals[nextGoalIndex].goalId : null;

        if (
            nextChartType === ChartType.DETAIL &&
            nextPeriodTypeId === PeriodType.DAY
        ) {
            nextPeriodTypeId = PeriodType.ROLLING_30;
        }

        if (
            userId === this.state.userId &&
            nextChartType === this.state.chartType &&
            nextPeriodTypeId === this.state.periodTypeId &&
            nextMetricId === this.state.metricId &&
            nextGoalId === this.state?.goalId &&
            nextCommitmentId === this.state.commitmentId
        ) {
            return;
        }

        if (nextMetricId == null) {
            useDispatcher(
                this,
                selectChart({
                    chartType: nextChartType,
                    periodTypeId: nextPeriodTypeId,
                }),
            );
            return;
        }

        if (nextChartType === ChartType.DETAIL) {
            useDispatcher(
                this,
                reloadDetail({
                    userId,
                    periodTypeId: nextPeriodTypeId,
                    metricId: nextMetricId,
                }),
            );
        } else {
            useDispatcher(
                this,
                reloadChart({
                    userId,
                    chartType: nextChartType,
                    periodTypeId: nextPeriodTypeId,
                    metricId: nextMetricId,
                    commitmentId: nextCommitmentId,
                    goalId: nextGoalId,
                }),
            );
        }
    }

    private _watchForSiblingPillars(): void {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const run = (metricId: number) => {
            const nextMetricId =
                metricId == null || isNaN(metricId) ? undefined : metricId;
            this._checkData(null);
            this._dispatch(undefined, null, nextMetricId);
        };

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const reSubB = (metricId: number, wasNotReady: boolean) => {
            const obses: Array<
                Observable<OpportunityView | CommitmentSummaryView>
            > = [];

            const oppObs =
                this._switchboardService.pillarOne.sbSelectedOpportunity();
            if (oppObs) {
                obses.push(oppObs);
            }

            const cmtObs =
                this._switchboardService.pillarTwo.sbSelectedOpportunityActiveCommitment();
            if (cmtObs) {
                obses.push(cmtObs);
            }

            return combineLatest(obses).pipe(
                // maybe just change sbSelectedOpportunity() and sbSelectedOpportunityActiveCommitment() to regular subjects
                skip(wasNotReady ? 1 : 0),
                distinctUntilChanged((x, y) => {
                    return x[0] === y[0] || x[1] === y[1];
                }),
                debounceTime(50),
                tap<[OpportunityView, CommitmentSummaryView]>(([opp]) => {
                    this._opportunity = opp;
                    run(metricId);
                }),
                take(1),
            );
        };

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const reSubA = (wasNotReady: boolean) => {
            return watchForNumberParamChange(
                this._activatedRoute,
                'metricId',
                null,
                false,
            ).pipe(
                tap((metricId) => {
                    // this is for when reSubB() might not emit anything
                    if (metricId == null) {
                        run(metricId);
                        this._siblingPillarsReady = true;
                    }
                }),
                switchMap((metricId) => {
                    return reSubB(metricId, wasNotReady);
                }),
                // eslint-disable-next-line no-return-assign
                tap(() => (this._siblingPillarsReady = true)),
            );
        };

        let pillarOneWasNotReady = false;
        const pillarOneObs = (
            !this._switchboardService.pillarOne.connected()
                ? this._switchboardService.observableForConnect().pipe(
                      filter(() =>
                          this._switchboardService.pillarOne.connected(),
                      ),
                      take(1),
                  )
                : of(null)
        ).pipe(
            switchMap(() => this._switchboardService.pillarOne.sbReady()),
            tap((val) => {
                if (!pillarOneWasNotReady && !val) {
                    pillarOneWasNotReady = true;
                }
            }),
            filter((val) => val === true),
            take(1),
        );

        let pillarTwoWasNotReady = false;
        const pillarTwoObs = (
            !this._switchboardService.pillarTwo.connected()
                ? this._switchboardService.observableForConnect().pipe(
                      filter(() =>
                          this._switchboardService.pillarTwo.connected(),
                      ),
                      take(1),
                  )
                : of(null)
        ).pipe(
            switchMap(() => this._switchboardService.pillarTwo.sbReady()),
            tap((val) => {
                if (!pillarTwoWasNotReady && !val) {
                    pillarTwoWasNotReady = true;
                }
            }),
            filter((val) => val === true),
            take(1),
        );

        forkJoin([pillarOneObs, pillarTwoObs])
            .pipe(
                switchMap(() =>
                    reSubA(pillarOneWasNotReady || pillarTwoWasNotReady),
                ),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
